import { useCallback, useEffect, useRef, useState } from 'react';

import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { createFinalizeNavigate } from '@/features/auth/shared/utils/createFinalizeNavigate';
import { createSignUp } from '@/features/auth/shared/utils/createSignUp';
import { useClerk, useSignUp } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

interface AcceptInvitationFormValues {
  firstName: string;
  lastName: string;
  password: string;
}

export const useAcceptInvitation = () => {
  const { signUp } = useSignUp();
  const clerk = useClerk();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<'loading' | 'form' | 'error' | 'migrated'>('loading');
  const [error, setError] = useState<AuthError | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const ticketProcessedRef = useRef(false);

  const ticket = searchParams.get('__clerk_ticket');
  const clerkStatus = searchParams.get('__clerk_status');

  const processTicket = useCallback(async () => {
    try {
      const { error: ticketError } = await createSignUp(signUp, { strategy: 'ticket', ticket: ticket ?? '' });

      if (ticketError) {
        setError(mapClerkError(ticketError, t));
        setStep('error');
        return;
      }

      setEmail(signUp.emailAddress ?? null);
      setStep('form');
    } catch (err) {
      setError(mapClerkError(err, t));
      setStep('error');
    }
  }, [signUp, ticket, t]);

  useEffect(() => {
    if (!clerk.loaded || ticketProcessedRef.current) return;

    if (!ticket) {
      ticketProcessedRef.current = true;
      setStep('error');
      return;
    }

    if (clerkStatus === 'sign_in') {
      setStep('migrated');
      ticketProcessedRef.current = true;
      return;
    }

    ticketProcessedRef.current = true;
    void processTicket();
  }, [clerk.loaded, ticket, clerkStatus, processTicket]);

  const handleSubmit = useCallback(
    async (data: AcceptInvitationFormValues) => {
      setLoading(true);
      setError(null);

      try {
        const { error: completeError } = await createSignUp(signUp, {
          strategy: 'ticket',
          ticket: ticket ?? '',
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password
        });

        if (completeError) {
          setError(mapClerkError(completeError, t));
          return false;
        }

        if (signUp.status === 'complete') {
          await signUp.finalize({ navigate: createFinalizeNavigate(navigate) });
          toast.success(t('success.auth.acceptInvitation'));
          return true;
        }

        setError({ type: 'general', message: t('error.acceptInvitation.unexpectedStatus') });
        return false;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [signUp, ticket, navigate, t]
  );

  return {
    step,
    error,
    loading,
    isLoaded: clerk.loaded,
    email,
    hasTicket: !!ticket,
    handleSubmit
  };
};
